import { Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface Step {
  label: string;
  enabled: boolean;
}

interface WizardProgressIndicatorProps {
  steps: Step[];
  currentStep: number;
  onStepClick: (step: number) => void;
}

export function WizardProgressIndicator({
  steps,
  currentStep,
  onStepClick,
}: WizardProgressIndicatorProps) {
  return (
    <div className="w-full py-6">
      <div className="flex items-center justify-between">
        {steps.map((step, index) => {
          const isActive = index === currentStep;
          const isCompleted = index < currentStep;
          const isEnabled = step.enabled;

          return (
            <div key={index} className="flex items-center flex-1">
              <div className="flex flex-col items-center flex-1">
                <button
                  type="button"
                  onClick={() => isEnabled && onStepClick(index)}
                  disabled={!isEnabled}
                  className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all",
                    isActive &&
                      "bg-primary text-primary-foreground ring-4 ring-primary/20",
                    isCompleted && "bg-green-600 text-white",
                    !isActive &&
                      !isCompleted &&
                      isEnabled &&
                      "bg-slate-200 text-slate-600 hover:bg-slate-300",
                    !isEnabled &&
                      "bg-slate-100 text-slate-400 cursor-not-allowed opacity-50",
                  )}
                >
                  {isCompleted ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </button>
                <span
                  className={cn(
                    "mt-2 text-xs font-medium text-center max-w-[100px] transition-colors",
                    isActive && "text-primary",
                    isCompleted && "text-green-600",
                    !isActive && !isCompleted && isEnabled && "text-slate-600",
                    !isEnabled && "text-slate-400",
                  )}
                >
                  {step.label}
                </span>
                {!isEnabled && (
                  <span className="mt-1 text-xs text-slate-400 italic">
                    (désactivée)
                  </span>
                )}
              </div>

              {index < steps.length - 1 && (
                <div className="flex-1 h-0.5 mx-2 -mt-8">
                  <div
                    className={cn(
                      "h-full transition-colors",
                      isCompleted ? "bg-green-600" : "bg-slate-200",
                    )}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-6 flex items-center justify-center gap-2">
        <div className="h-2 bg-slate-200 rounded-full flex-1 max-w-md overflow-hidden">
          <div
            className="h-full bg-primary transition-all duration-300 ease-out"
            style={{
              width: `${((currentStep + 1) / steps.length) * 100}%`,
            }}
          />
        </div>
        <span className="text-sm font-medium text-slate-600">
          {currentStep + 1} / {steps.length}
        </span>
      </div>
    </div>
  );
}
